import client from '../client';
import type { ApiResponse } from '../../types/api';

/**
 * Cards contributed by installed apps.
 *
 * The shape is fixed by the platform, not by the app: an app supplies a title,
 * a few stats, one optional note and one optional link, and the dashboard draws
 * all of it. No markup or URLs to fetch cross this boundary, so a third-party
 * app can neither restyle the page nor run anything in it.
 */
export type AppWidgetStat = {
  label: string;
  value: string | number;
  /** Change since the app's own comparison window; sign carries the direction. */
  delta?: number;
  hint?: string;
};

export type AppWidgetRow = {
  label: string;
  value?: string | number;
  hint?: string;
  href?: string;
};

/**
 * One thing standing between the merchant and this app working.
 *
 * `done` is the app's fact, recomputed on every push. `skipped` is the
 * merchant's own decision, stored on the store by zero and merged in on read,
 * so the app republishing its snapshot can never undo it. A `required` step
 * offers no skip.
 */
export type AppWidgetStep = {
  key: string;
  label: string;
  hint?: string;
  done: boolean;
  skipped?: boolean;
  required?: boolean;
  action?: { label: string; href: string };
};

export type AppWidget = {
  /** `<app-slug>:<key>` — what the merchant's hide list keys on. */
  id: string;
  key: string;
  title: string;
  icon: string;
  order: number;
  stats: AppWidgetStat[];
  note?: string;
  /** One thing needing attention, rather than reporting. */
  alert?: { text: string; tone: 'info' | 'warn' | 'success'; action?: { label: string; href: string } };
  rows?: AppWidgetRow[];
  rowsTitle?: string;
  steps?: AppWidgetStep[];
  stepsTitle?: string;
  action?: { label: string; href: string };
  hidden: boolean;
  app: { name: string; slug: string | null; logoUrl: string | null };
  /** When the app last pushed. Shown when it is old enough to matter. */
  updatedAt: string;
};

export const appWidgetsApi = {
  list: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ widgets: AppWidget[] }>>(
      `/dashboard/${storeId}/app-widgets`,
    );
    return res.data.data?.widgets ?? [];
  },

  setHidden: async (storeId: string, hidden: string[]) => {
    await client.patch(`/dashboard/${storeId}/app-widgets/prefs`, { hidden });
  },

  /** Full replacement of the skip list, keyed `<widget id>:<step key>`. */
  setSkippedSteps: async (storeId: string, skipped: string[]) => {
    await client.patch(`/dashboard/${storeId}/app-widgets/steps`, { skipped });
  },
};
